import type {
  AudienceScope,
  DepartmentAudience,
  FacilityAudience,
  PermissionAudience,
  PersonAudience,
  PositionAudience,
  RelativePositionAudience,
  RoleAudience,
  TeamAudience,
} from "@common/client"
import { sentenceCase } from "@common/lib/sentences"
import {
  Building2,
  IdCard,
  KeyRound,
  type LucideIcon,
  Network,
  ShieldCheck,
  UserRound,
  UsersRound,
} from "lucide-react"

export type Names = Record<string, string>

export type AudienceItem = {
  icon: LucideIcon
  label: string
  detail: string
  missing: boolean
}

export type ConditionValue = {
  text: string
  missing: boolean
}

export type ConditionPhrase = {
  lead: string
  values: ConditionValue[]
}

export type RecordKind =
  | "user"
  | "team"
  | "department"
  | "facility"
  | "customer"
  | "aircraft"
  | "work_order"

export type StaffAudience =
  | PersonAudience
  | PositionAudience
  | TeamAudience
  | DepartmentAudience
  | FacilityAudience
  | RoleAudience
  | PermissionAudience
  | RelativePositionAudience

const RESOURCE_NOUNS: Record<string, string> = {
  atl_page: "tech log pages",
  canned_work_package: "templates",
  maintenance_programme: "maintenance programs",
  part_balance: "inventory",
  tools: "tools",
  work_package: "work orders",
}

export function spoken(value: string): string {
  return sentenceCase(value.replaceAll("_", " "))
}

export function texts(value: string | string[] | boolean): string[] {
  return Array.isArray(value) ? value : typeof value === "string" ? [value] : []
}

export function phrase(lead: string, values: string[]): ConditionPhrase {
  return { lead, values: values.map((text) => ({ text, missing: false })) }
}

export function namedPhrase(
  lead: string,
  ids: string[],
  names: Names,
  removed: string,
): ConditionPhrase {
  return {
    lead,
    values: ids.map((id) => ({
      text: names[id] ?? removed,
      missing: names[id] === undefined,
    })),
  }
}

export function phraseText(entry: ConditionPhrase): string {
  return [entry.lead, ...entry.values.map((value) => value.text)].join(" ")
}

export function permissionLabel(permission: string): string {
  const [resource = "", action = ""] = permission.split(":")
  const noun = RESOURCE_NOUNS[resource] ?? `${resource.replaceAll("_", " ")}s`
  return action === "*" ? `Full access to ${noun}` : `${spoken(action)} ${noun}`
}

function scopeDetail(scope: AudienceScope, actor: string): string {
  switch (scope) {
    case "actor_team":
      return `In the team of ${actor}`
    case "actor_department":
      return `In the department of ${actor}`
    case "actor_facility":
      return `At the facility of ${actor}`
    case "subject_department":
      return "In the department the record belongs to"
    case "subject_facility":
      return "At the facility the record belongs to"
  }
}

export function named(
  icon: LucideIcon,
  name: string | undefined,
  detail: string,
  removed: string,
): AudienceItem {
  return {
    icon,
    label: name ?? removed,
    detail,
    missing: name === undefined,
  }
}

export function staffAudienceItem(
  audience: StaffAudience,
  names: Names,
  actor: string,
): AudienceItem {
  switch (audience.kind) {
    case "person":
      return named(
        UserRound,
        names[audience.user_id],
        "Person",
        "Removed person",
      )
    case "team":
      return named(UsersRound, names[audience.team_id], "Team", "Archived team")
    case "department":
      return named(
        Network,
        names[audience.department_id],
        "Department",
        "Archived department",
      )
    case "facility":
      return named(
        Building2,
        names[audience.facility_id],
        "Facility",
        "Archived facility",
      )
    case "role":
      return named(
        ShieldCheck,
        names[audience.role_id],
        "Everyone with this role",
        "Deleted role",
      )
    case "position":
      return named(
        IdCard,
        names[audience.position_id],
        "Whoever holds this position",
        "Removed position",
      )
    case "permission":
      return {
        icon: KeyRound,
        label: permissionLabel(audience.permission),
        detail: "Anyone whose role allows it",
        missing: false,
      }
    case "relative_position":
      return {
        icon: IdCard,
        label: audience.position_name,
        detail: scopeDetail(audience.scope, actor),
        missing: false,
      }
  }
}
